import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import FormInputBox from "../../../components/Input/FormInputBox";
import {
  blackColor,
  greyColor6,
  whiteColor,
} from "../../../configs/styles/muiThemes";
import ActionButtons from "../../../components/Buttons/ActionButtons";
import StyledSwitch from "../../../components/Switch/StyledSwitch";
import SelectWithLabel from "../../../components/Input/SelectWithLabel";
import {
  FULL_CANCEL_STAGE,
  PARTIAL_CANCEL_STAGE,
  SHIPMENT_REGION_LIST,
} from "../../../utils/data";
import RadioOptionWithLabel from "../../../components/Radio/RadioOptionWithLabel";
import RadiosBtnWithLabel from "../../../components/Input/RadiosBtnWithLabel";
import DateInput from "../../../components/Input/DateInput";
import ONDCCatMultiSelect from "../ONDCCatMultiSelect";

import { ONDC_ECOM_DEFAULT_DATA } from "../../../utils/data/defaultFormData";
import DashboardStepper from "../../Dashboard/DashboardStepper";
import { ECOMMERCE_STEP_LIST, ONDC_STEP_LIST } from "../../../utils/appData";

function ONDCControlsForm({
  data,
  setData,
  ondcCategories,
  onValueChange,
  currentPage,
  gotoNext,
  goBack,
  handleCancel,
}) {
  const handleOndcInfoChange = (property, value) => {
    setData((prevState) => {
      const ondcUpdate = {
        ...prevState.ondc,
        [property]: value,
      };

      return {
        ...prevState,
        ondc: ondcUpdate,
        errors: { ...prevState?.errors, [property]: "" },
      };
    });
  };

  const handleDeliveryMethodChange = (id, deliveryMethod) => {
    // console.log("deliveryMethod:", { id, deliveryMethod });
    handleOndcInfoChange("ondcBPPFulfillments", { id, type: deliveryMethod });
  };

  // handle auto approve
  const handleAutoApproveInput = (inputValue) => {
    setData((prevState) => ({
      ...prevState,
      ondc: {
        ...prevState.ondc,
        autoApproval: {
          ...prevState?.ondc?.autoApproval,
          autoApprovalEnabled: inputValue,
        },
      },
      errors: { ...prevState?.errors, autoApprovalEnabled: "" },
    }));
  };

  const handleONDCCategoryChange = (value) => {
    // console.log("ONDC_Cat:", value);

    handleOndcInfoChange("ondcDomain", value);
  };

  return (
    <Box sx={styles.card}>
      <Typography
        component="h6"
        fontSize={{ xs: 15, md: 16 }}
        color={blackColor}
        fontWeight={600}
        lineHeight="20px"
        mt={{ xs: "15px !important", md: "20px !important" }}
      >
        ONDC Controls
      </Typography>

      <Box>
        <Box
          mt={{ xs: "15px !important", md: "10px !important" }}
          mb={"5px !important"}
        >
          <Typography
            component="h6"
            fontSize={{ xs: 12, md: 13 }}
            color={greyColor6}
            fontWeight={400}
            lineHeight="20px"
            display="inline"
          >
            ONDC Enabled{" "}
            <Typography
              variant="body1"
              sx={{ display: "inline", color: "#f00" }}
            >
              *
            </Typography>
          </Typography>
        </Box>

        <StyledSwitch
          label={data?.ondcEnabled === "yes" ? "Yes" : "No"}
          isChecked={data?.ondcEnabled === "yes"}
          onChange={() => {
            if (data?.ondcEnabled === "yes") {
              setData((prevState) => ({
                ...ONDC_ECOM_DEFAULT_DATA,
                ondcEnabled: "no",
                errors: null,
              }));

              return;
            }
            setData((prevState) => ({
              ...prevState,
              ondcEnabled: data?.ondcEnabled === "yes" ? "no" : "yes",
            }));
          }}
        />

        {data?.ondcEnabled === "yes" && (
          <>
            <DashboardStepper
              stepList={ONDC_STEP_LIST}
              // currentPage={currentPage}
              isAddProduct
            />

            <Box mt={2}>
              <Grid container rowSpacing={0} columnSpacing={3}>
                <Grid item xs={12} md={5}>
                  <ONDCCatMultiSelect
                    label="ONDC Category"
                    value={data?.ondc?.ondcDomain || []}
                    onValueChange={handleONDCCategoryChange}
                    menuList={ondcCategories}
                    required
                    error={data?.errors?.ondcDomain}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={2}>
              <Typography component="h6" sx={styles.sectionHeader}>
                Shipments & Discounts
              </Typography>

              <Grid container rowSpacing={0} columnSpacing={3}>
                <Grid item xs={12} md={5}>
                  <SelectWithLabel
                    label="Shipment Region"
                    value={data?.ondc?.ondcSupportRegion?.label}
                    onValueChange={(region) => {
                      handleOndcInfoChange("ondcSupportRegion", region);
                    }}
                    returnFullItem
                    menuList={SHIPMENT_REGION_LIST}
                    errorMsg={data?.errors?.ondcSupportRegion}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormInputBox
                    label="Shipment Distance (in KM)"
                    placeholder=""
                    required
                    value={data?.ondc?.ondcSupportDistanceKm}
                    onChange={({ target }) => {
                      const value = target?.value?.replace(/[^0-9]/g, "");
                      handleOndcInfoChange("ondcSupportDistanceKm", value);
                    }}
                    error={data?.errors?.ondcSupportDistanceKm}
                    helperText="For Hyperlocal delivery choose distance as 5KM"
                  />
                </Grid>

                <Grid item xs={12} md={3}></Grid>

                <Grid item xs={12} md={5}>
                  <Typography
                    component="p"
                    fontSize={13}
                    fontWeight={400}
                    color={greyColor6}
                    display="inline"
                  >
                    Delivery Methods
                    <Typography
                      variant="body1"
                      sx={{ display: "inline", color: "#f00" }}
                    >
                      *
                    </Typography>
                  </Typography>

                  <Box>
                    <RadiosBtnWithLabel
                      label="Delivery"
                      checked={
                        data?.ondc?.ondcBPPFulfillments?.type === "Delivery"
                      }
                      onValuesChanged={() => {
                        handleDeliveryMethodChange("F1", "Delivery");
                      }}
                      labelStyles={styles.radioLabel}
                    />

                    <RadiosBtnWithLabel
                      label="Self-Pickup"
                      checked={
                        data?.ondc?.ondcBPPFulfillments?.type === "Self-Pickup"
                      }
                      onValuesChanged={() => {
                        handleDeliveryMethodChange("F2", "Self-Pickup");
                      }}
                      labelStyles={styles.radioLabel}
                    />

                    <RadiosBtnWithLabel
                      label="Delivery and Self-Pickup"
                      checked={
                        data?.ondc?.ondcBPPFulfillments?.type ===
                        "Delivery and Self-Pickup"
                      }
                      onValuesChanged={() => {
                        handleDeliveryMethodChange(
                          "F3",
                          "Delivery and Self-Pickup"
                        );
                      }}
                      labelStyles={styles.radioLabel}
                    />
                  </Box>

                  <Typography
                    variant="subtitle1"
                    component="p"
                    fontSize={12}
                    sx={{ color: "red" }}
                  >
                    {data?.errors?.ondcBPPFulfillments}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <RadioOptionWithLabel
                    label="Customer Discount"
                    required
                    value={
                      data?.ondc?.ondcCustomerDiscountEnabled === ""
                        ? ""
                        : data?.ondc?.ondcCustomerDiscountEnabled === "yes"
                        ? "Yes"
                        : "No"
                    }
                    onYesClick={() => {
                      handleOndcInfoChange(
                        "ondcCustomerDiscountEnabled",
                        "yes"
                      );
                    }}
                    onNoClick={() => {
                      handleOndcInfoChange("ondcCustomerDiscountEnabled", "no");
                    }}
                    error={data?.errors?.ondcCustomerDiscountEnabled}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={2}>
              <Typography component="h6" sx={styles.sectionHeader}>
                Other Info
              </Typography>

              <Grid container rowSpacing={0} columnSpacing={3}>
                <Grid item xs={12} md={3.5}>
                  <FormInputBox
                    label="Minimum Order Amount"
                    placeholder=""
                    startIcon="₹"
                    required
                    value={data?.ondc?.ondcMinOrder}
                    onChange={({ target }) => {
                      const value = target?.value?.replace(/[^0-9]/g, "");
                      handleOndcInfoChange("ondcMinOrder", value);
                    }}
                    error={data?.errors?.ondcMinOrder}
                    helperText="Recommended value is average product price"
                  />
                </Grid>

                <Grid item xs={12} md={3.2}>
                  <FormInputBox
                    label="Packing Cost"
                    placeholder=""
                    startIcon="₹"
                    required
                    value={data?.ondc?.packagingCost}
                    onChange={({ target }) => {
                      const value = target?.value?.replace(/[^0-9]/g, "");
                      handleOndcInfoChange("packagingCost", value);
                    }}
                    error={data?.errors?.packagingCost}
                  />
                </Grid>

                <Grid item xs={12} md={3.2}>
                  <FormInputBox
                    label="Convenience Fee"
                    placeholder=""
                    startIcon="₹"
                    required
                    value={data?.ondc?.convenienceFee}
                    onChange={({ target }) => {
                      const value = target?.value?.replace(/[^0-9]/g, "");
                      handleOndcInfoChange("convenienceFee", value);
                    }}
                    error={data?.errors?.convenienceFee}
                  />
                </Grid>

                <Grid item xs={12} md={9.9}>
                  <FormInputBox
                    label="Buyer Finder Fee Limit %"
                    placeholder="Min 3 to Max 10"
                    required
                    value={data?.ondc?.ondcBuyerFinderFeeUpperLimitPercentage}
                    onChange={({ target }) => {
                      const value = target?.value?.replace(/[^0-9]/g, "");
                      handleOndcInfoChange(
                        "ondcBuyerFinderFeeUpperLimitPercentage",
                        value
                      );
                    }}
                    error={data?.errors?.ondcBuyerFinderFeeUpperLimitPercentage}
                    helperText="If the Buyer app finder fee is more than specified here, your products will not he displayed in customer search"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={2}>
              <Typography component="h6" sx={styles.sectionHeader}>
                Cancel Terms
              </Typography>

              <Grid container rowSpacing={2} columnSpacing={3}>
                <Grid item xs={12} md={3}>
                  <SelectWithLabel
                    label="Partial Cancel Stage"
                    value="Order Created"
                    required
                    onValueChange={() => {}}
                    returnFullItem
                    menuList={PARTIAL_CANCEL_STAGE}
                    errorMsg=""
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <SelectWithLabel
                    label="Full Cancel Stage"
                    value={
                      data?.ondc?.cancelTerms?.ondcFullCancellationStage?.label
                    }
                    onValueChange={(status) => {
                      setData((prevState) => ({
                        ...prevState,
                        ondc: {
                          ...prevState.ondc,
                          cancelTerms: {
                            ...prevState?.ondc?.cancelTerms,
                            ondcFullCancellationStage: status,
                          },
                        },

                        errors: {
                          ...prevState?.errors,
                          ondcFullCancellationStage: "",
                        },
                      }));
                    }}
                    required
                    returnFullItem
                    menuList={FULL_CANCEL_STAGE}
                    errorMsg={data?.errors?.ondcFullCancellationStage}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={3}>
              <Typography component="h6" sx={styles.sectionHeader}>
                Auto Approval
              </Typography>

              <Grid container rowSpacing={2} columnSpacing={3}>
                <Grid item xs={12} md={3}>
                  <RadioOptionWithLabel
                    label="Auto Approval"
                    required
                    value={
                      data?.ondc?.autoApproval?.autoApprovalEnabled === ""
                        ? ""
                        : data?.ondc?.autoApproval?.autoApprovalEnabled === true
                        ? "Yes"
                        : "No"
                    }
                    onYesClick={() => {
                      handleAutoApproveInput(true);
                    }}
                    onNoClick={() => {
                      handleAutoApproveInput(false);
                    }}
                    error={data?.errors?.autoApprovalEnabled}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box mt={2}>
              <Typography
                component="h6"
                sx={{ ...styles.sectionHeader, mb: 0.8 }}
              >
                Holiday Schedule
              </Typography>

              <Typography
                component="h6"
                sx={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: greyColor6,
                  mb: 1.5,
                }}
              >
                Enter any planned holidays in next 3 months, when the store
                won't be operational
              </Typography>

              <Grid container rowSpacing={2} columnSpacing={3}>
                <Grid item xs={12} md={3}>
                  <DateInput
                    label="Start Date"
                    // date={
                    //   data?.ondc?.fssai?.[0]?.validTill
                    //     ? moment(
                    //         data?.ondc?.fssai?.[0]?.validTill
                    //       ).format("yyyy-MM-DD")
                    //     : ""
                    // }
                    // onDateChange={({ target }) => {
                    //   // onValueChange("endDate", moment(target.value).valueOf());
                    //   setData((prevState) => ({
                    //     ...prevState,
                    //     ondc: {
                    //       ...prevState?.ondc,
                    //       fssai: [
                    //         {
                    //           ...prevState?.ondc?.fssai?.[0],
                    //           validTill: moment(target.value).valueOf(),
                    //         },
                    //       ],
                    //     },
                    //     errors: {
                    //       ...prevState?.errors,
                    //       validTill: "",
                    //     },
                    //   }));
                    // }}
                    // error={data?.errors?.validTill}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DateInput
                    label="End Date"
                    // date={
                    //   data?.ondc?.fssai?.[0]?.validTill
                    //     ? moment(
                    //         data?.ondc?.fssai?.[0]?.validTill
                    //       ).format("yyyy-MM-DD")
                    //     : ""
                    // }
                    // onDateChange={({ target }) => {
                    //   // onValueChange("endDate", moment(target.value).valueOf());
                    //   setData((prevState) => ({
                    //     ...prevState,
                    //     ondc: {
                    //       ...prevState?.ondc,
                    //       fssai: [
                    //         {
                    //           ...prevState?.ondc?.fssai?.[0],
                    //           validTill: moment(target.value).valueOf(),
                    //         },
                    //       ],
                    //     },
                    //     errors: {
                    //       ...prevState?.errors,
                    //       validTill: "",
                    //     },
                    //   }));
                    // }}
                    // error={data?.errors?.validTill}
                  />
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>

      <Box mt="20px">
        <ActionButtons
          // onBackClick={goBack}
          onSaveClick={() => {}}
          onNextClick={gotoNext}
          onCancelClick={handleCancel}
          // showBackBtn
          hideSaveBtn
          hideCancel
          backBtnText="Previous"
          nextBtnText="PREVIEW & SUBMIT"
        />
      </Box>
    </Box>
  );
}

export default ONDCControlsForm;

const styles = {
  card: {
    backgroundColor: whiteColor,
    borderRadius: "13px",
    boxShadow: "0 0px 4px 0 rgb(0 0 0 / 5%)",
    p: "3px 15px 15px 15px",
    m: { xs: "15px 0 25px", md: "0px 0 25px" },
  },

  sectionHeader: {
    fontSize: { xs: 15, md: 15 },
    color: blackColor,
    fontWeight: 600,
    lineHeight: "20px",
    mb: 2,
  },

  radioLabel: {
    color: greyColor6,
    fontSize: 13,
    fontWeight: 400,
  },
};
